import React, { useState } from 'react';
import { buttonize } from 'venn-utils';
import styled from 'styled-components';
import type { AnalysisView } from 'venn-api';
import type { StudioSidePanelContextProps } from 'venn-components';
import { LearnMore } from 'venn-components';
import { Icon, GetColor, ColorUtils, STUDIO_FAQ_HREF } from 'venn-ui-kit';
import { useRecoilValue } from 'recoil';
import { blockDisplayHeader, isLinked } from 'venn-state';

type GlobalLinkedInfoProps = {
  globalView?: AnalysisView;
} & Pick<StudioSidePanelContextProps, 'onSelectBlock'>;

const UnlinkedItem = ({ id, onClick }: { id: string; onClick: () => void }) => {
  const linked = useRecoilValue(isLinked(id));
  const header = useRecoilValue(blockDisplayHeader(id));

  if (linked) {
    return null;
  }

  return (
    <StyledList onClick={onClick}>
      <BlockHeader>{header}</BlockHeader>
      <SettingIcon type="sliders-h" />
    </StyledList>
  );
};

const GlobalLinkedInfo = ({ globalView, onSelectBlock }: GlobalLinkedInfoProps) => {
  const [expandUnLinkedBlock, setExpandUnLinkedBlock] = useState(false);

  return (
    <GlobalLinkedSection>
      <Flex fontSize="1rem">
        <Icon type="chart-network" />
        <Message>
          All blocks <Bold>link to this setup </Bold>by default. <LearnMore href={STUDIO_FAQ_HREF} />
        </Message>
      </Flex>
      <SmallEmphasis {...buttonize(() => setExpandUnLinkedBlock(!expandUnLinkedBlock))}>
        <Flex fontSize="calc(1rem * 5 / 6)">
          <Icon type="unlink" />
          <span>Unlinked blocks</span>
          <Icon type={expandUnLinkedBlock ? 'caret-up' : 'caret-down'} />
        </Flex>
      </SmallEmphasis>
      {expandUnLinkedBlock ? (
        <UnLinkedListWrapper>
          {globalView?.customizedViews?.map((view) =>
            view.refId ? (
              <UnlinkedItem
                key={view.refId}
                id={view.refId}
                onClick={() => {
                  onSelectBlock(view.refId, { scrollIntoView: true });
                }}
              />
            ) : null,
          )}
        </UnLinkedListWrapper>
      ) : null}
    </GlobalLinkedSection>
  );
};

export default GlobalLinkedInfo;

const Flex = styled.div<{ fontSize: string }>`
  display: flex;
  ${({ fontSize }) => `font-size: ${fontSize};`}
`;

const StyledList = styled.button`
  display: flex;
  color: ${GetColor.Black};
  width: 100%;
  :hover {
    cursor: pointer;
    background: ${GetColor.Primary.Light};
    color: ${GetColor.Primary.Dark};
    i {
      color: ${GetColor.Primary.Dark};
    }
  }
`;

export const Emphasis = styled.div`
  font-style: italic;
  font-size: 1rem;
  color: ${GetColor.GreyScale.Grey80};
  cursor: pointer;
`;

const SmallEmphasis = styled(Emphasis)`
  font-size: 10px;
  margin-top: 5px;

  span {
    margin: 0 5px;
  }
`;

export const Message = styled.div`
  font-size: 1rem;
  flex: 1;
  margin: 0 5px;
`;

export const Bold = styled.span`
  font-weight: bold;
`;

const GlobalLinkedSection = styled.div`
  background-color: ${ColorUtils.opacifyFrom(GetColor.GreyScale.Grey10, 0.5)};
  padding: 5px 15px;
`;

const UnLinkedListWrapper = styled.div`
  padding: 15px 0px;
`;

const BlockHeader = styled.div`
  line-height: 20px;
  flex: 1;
  display: flex;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SettingIcon = styled(Icon)`
  color: ${GetColor.GreyScale.Grey70};
  line-height: 20px;
  margin-left: 10px;
`;
